import React, { useState } from 'react';
import { InitialQuestions } from './InitialQuestions';
import { homePageInitialState, useChatReasonContext, useHomePageContext } from '../../context/HomePageContext';
import {
  buildInitialCommsCheckMessage,
  CommsCheck,
  determineCommsCheckResultForReporting,
  filterDevicesForEsmeGsme,
} from '../commsCheck/CommsCheck';
import {
  DecommissionReasonContext,
  DecommissionReasonType,
  DecommissionRequestContext,
  DecommissionRequestType,
  DecommissionResultContext,
  DecommissionResultType,
  DecommissionScreenContext,
  DecommissionScreenType,
  useDecommissionInputsContext,
} from '../../context/DecommissionContext';
import commsCheckInitialData, { CommsCheckContext } from '../../context/CommsCheckContext';
import { CommsCheckResult, XrinvData } from '../../types/comms-check';
import { InlineButtonContainer } from '../common/inlineButtonContainer/InlineButtonContainer';
import { Button, ButtonTheme } from '../common/button/Button';
import { startChatSession } from '../chat/startChatSession';
import { getAuthTokenOrLogOut, reportingUtils } from '../util';
import SelectDecommissionReason from './SelectDecommissionReason';
import DecommissionSummary from './DecommissionSummary';
import { DecommissionLoading } from './DecommissionLoading';
import { DecommissionResult } from './DecommissionResult';
import { reportingClient } from '../../api/atomProxyClient';

export default function DecommissionAMeter() {
  const { homePageState, setHomePageState } = useHomePageContext();
  const { decommissionInputs, setDecommissionInputs } = useDecommissionInputsContext();
  const { chatReason } = useChatReasonContext();
  const [commsCheckResultContext, setCommsCheckResultContext] = useState<CommsCheckResult>(commsCheckInitialData);

  const [screen, setScreen] = useState<DecommissionScreenType>(DecommissionScreenType.INITIAL);
  const [reason, setReason] = useState<DecommissionReasonType>();
  const [decommissionResult, setDecommissionResult] = useState<DecommissionResultType>({
    status: '',
    deviceType: '',
    deviceId: '',
  });

  const INITIAL_DECOMMISSION_REQUEST = {
    deviceType: '',
    deviceId: '',
    mpxn: '',
    chfDeviceId: '',
    decommissionJourneyId: '',
    commsCheckStartTimeStamp: new Date(new Date().setHours(0, 0, 0, 0)),
    decommissionStartTimeStamp: new Date(new Date().setHours(0, 0, 0, 0)),
  };
  const [decommissionRequest, setDecommissionRequest] = useState<DecommissionRequestType>(INITIAL_DECOMMISSION_REQUEST);

  const filteredDevicesList = filterDevicesForEsmeGsme(commsCheckResultContext.xrinv.data);
  const isMetersActive = filteredDevicesList.every((device) => device.activeStatus === true);

  async function startCommsCheckHandler() {
    setScreen(DecommissionScreenType.COMMS_CHECK);
    setHomePageState({
      ...homePageState,
      automationInProgress: true,
    });
    setDecommissionRequest({
      ...decommissionRequest,
      decommissionJourneyId: crypto.randomUUID(),
      commsCheckStartTimeStamp: new Date(),
    });
  }

  function goBackHandler() {
    setHomePageState({
      ...homePageState,
      automationInProgress: false,
    });
    setCommsCheckResultContext(commsCheckInitialData);
    setScreen(DecommissionScreenType.INITIAL);
    setDecommissionInputs({
      ...decommissionInputs,
      onsiteRadioAnswer: '',
      meterTypeRadioAnswer: '',
    });
    setDecommissionRequest(INITIAL_DECOMMISSION_REQUEST);
  }

  async function reportCommsCheck(commsCheckResult: CommsCheckResult) {
    const commsCheckResultTimeStamp = new Date();
    const userDetails = await getAuthTokenOrLogOut();
    const requestBody = {
      decommissionJourneyId: decommissionRequest.decommissionJourneyId,
      mpxn: decommissionInputs.mpxn,
      msn: decommissionInputs.msn,
      commsCheckResultStatus: determineCommsCheckResultForReporting(commsCheckResult, isMetersActive),
      commsCheckStartingTimeStamp: decommissionRequest.commsCheckStartTimeStamp.toISOString(),
      commsCheckResultTimeStamp: commsCheckResultTimeStamp.toISOString(),
      commsCheckTimeTaken: commsCheckResultTimeStamp.getTime() - decommissionRequest.commsCheckStartTimeStamp.getTime(),
      contactReason: chatReason,
      engineerEmail: userDetails.userEmail ?? '',
    };

    // TODO: Report to sentry if any error
    reportingClient.commsCheck(requestBody).then();
  }

  async function reportDecommission() {
    const decommissionResultTimeStamp = new Date();
    const userDetails = await getAuthTokenOrLogOut();
    const requestBody = {
      decommissionJourneyId: decommissionRequest.decommissionJourneyId,
      mpxn: decommissionInputs.mpxn,
      msn: decommissionInputs.msn,
      assetType: reportingUtils.mapMeterTypeRadioAnswerAssetType(decommissionInputs.meterTypeRadioAnswer),
      contactReason: chatReason,
      engineerEmail: userDetails.userEmail ?? '',
      engineerOnsite: reportingUtils.mapOnsiteRadioAnswerToEngineerOnSite(decommissionInputs.onsiteRadioAnswer),
      decommissionReason: reason ?? '',
      decommissionResultStatus: decommissionResult.status,
      decommissionStartingTimeStamp: decommissionRequest.decommissionStartTimeStamp.toISOString(),
      decommissionResultTimeStamp: decommissionResultTimeStamp.toISOString(),
      decommissionTimeTaken: decommissionResultTimeStamp.getTime() - decommissionRequest.decommissionStartTimeStamp.getTime(),
    };

    // TODO: Report to sentry if any error
    reportingClient.decommission(requestBody).then();
  }

  async function startChatHandler() {
    const buildInitialChatMessage = buildInitialCommsCheckMessage(
      commsCheckResultContext,
      decommissionInputs.mpxn,
      decommissionInputs.msn,
      chatReason,
    );
    await startChatSession({ message: buildInitialChatMessage, mpxn: decommissionInputs.mpxn, chatReason });
  }

  function askAboutSomethingElseHandler() {
    setDecommissionResult({
      status: '',
      deviceType: '',
      deviceId: '',
    });
    setScreen(DecommissionScreenType.INITIAL);
    setHomePageState({
      ...homePageInitialState,
      brandSelected: homePageState.brandSelected,
      automationInProgress: false,
    });
    setDecommissionRequest(INITIAL_DECOMMISSION_REQUEST);
  }

  function continueToDecommission() {
    setScreen(DecommissionScreenType.SELECT_DECOMMISSION_REASON);
  }

  function renderActionButtons(data: CommsCheckResult) {
    if (data.xrinv.state && data.xritm.state && data.xrdlg.state) {
      if (data.xrinv.state === 'SUCCESS' && data.xritm.state === 'SUCCESS' && data.xrdlg.state === 'SUCCESS') {
        return (
          <InlineButtonContainer>
            <Button
              buttonId={'continue-decommission'}
              buttonText={'Continue to decommission'}
              buttonTheme={ButtonTheme.PRIMARY}
              onClickHandler={continueToDecommission}
            />
            {data.xrdlg.data.length ? (
              <Button
                buttonId={'start-chat-decommission'}
                buttonText={'Start Chat'}
                buttonTheme={ButtonTheme.PRIMARY}
                onClickHandler={startChatHandler}
              />
            ) : null}
            <Button
              buttonId={'go-back-decommission'}
              buttonText={'Go Back'}
              buttonTheme={ButtonTheme.SECONDARY}
              onClickHandler={goBackHandler}
            />
          </InlineButtonContainer>
        );
      } else {
        return (
          <InlineButtonContainer>
            <Button
              buttonId={'continue-decommission'}
              buttonText={'Continue to decommission'}
              buttonTheme={ButtonTheme.PRIMARY}
              onClickHandler={continueToDecommission}
              isButtonEnabled={data.xrinv.state === 'SUCCESS'}
            />
            <Button
              buttonId={'start-chat-decommission'}
              buttonText={'Start Chat'}
              buttonTheme={ButtonTheme.PRIMARY}
              onClickHandler={startChatHandler}
            />
            <Button
              buttonId={'go-back-decommission'}
              buttonText={'Go Back'}
              buttonTheme={ButtonTheme.SECONDARY}
              onClickHandler={goBackHandler}
            />
          </InlineButtonContainer>
        );
      }
    } else {
      if (homePageState.automationInProgress) {
        return (
          <InlineButtonContainer>
            <Button
              buttonId="go-back-decommission"
              buttonText="Go Back"
              buttonTheme={ButtonTheme.PRIMARY}
              onClickHandler={goBackHandler}
            />
          </InlineButtonContainer>
        );
      } else {
        return <></>;
      }
    }
  }

  function mapMeterTypeRadioAnswerToDeviceType(): string {
    switch (decommissionInputs.meterTypeRadioAnswer) {
      case 'Electric':
        return 'ESME';
      case 'Gas':
        return 'GSME';
      default:
        return '';
    }
  }

  function renderDecommissionSummary() {
    const device: XrinvData = commsCheckResultContext.xrinv.data.find(
      (xrinvData) => xrinvData.deviceType === mapMeterTypeRadioAnswerToDeviceType(),
    ) ?? {
      deviceType: '',
      deviceStatus: '',
      mpxn: '',
      deviceId: '',
      smetsDeviceType: '',
      lastCommunicatedTime: null,
      activeStatus: null,
    };

    const chfId = commsCheckResultContext.xrinv.data.find((xrinvData) => xrinvData.deviceType === 'CHF')?.deviceId ?? '';

    return (
      <DecommissionSummary
        deviceType={device.deviceType}
        smetsDeviceType={device.smetsDeviceType}
        deviceId={device.deviceId}
        chfDeviceId={chfId}
        reason={reason ?? ''}
        mpxn={device.mpxn}
      />
    );
  }

  return (
    <div>
      <DecommissionScreenContext.Provider value={{ screen, setScreen }}>
        {screen === DecommissionScreenType.INITIAL && <InitialQuestions primaryButtonHandler={startCommsCheckHandler} />}

        <DecommissionRequestContext.Provider value={{ decommissionRequest, setDecommissionRequest }}>
          <CommsCheckContext.Provider value={{ commsCheckResultContext, setCommsCheckResultContext }}>
            {screen === DecommissionScreenType.COMMS_CHECK && (
              <div className="commsCheckDecommFlexContainer">
                <div className="commsCheckDecommContent">
                  <CommsCheck
                    mpxn={decommissionInputs.mpxn}
                    msn={decommissionInputs.msn}
                    retryCommsCheck={1}
                    reportCommsCheck={reportCommsCheck}
                  />
                </div>
                {renderActionButtons(commsCheckResultContext)}
              </div>
            )}

            <DecommissionReasonContext.Provider
              value={{
                decommissionReason: reason,
                setDecommissionReason: setReason,
              }}
            >
              {screen === DecommissionScreenType.SELECT_DECOMMISSION_REASON && <SelectDecommissionReason />}
              {screen === DecommissionScreenType.DECOMMISSION_SUMMARY && renderDecommissionSummary()}
            </DecommissionReasonContext.Provider>
          </CommsCheckContext.Provider>

          <DecommissionResultContext.Provider value={{ decommissionResult, setDecommissionResult }}>
            {screen === DecommissionScreenType.DECOMMISSION_LOADING && <DecommissionLoading />}
            {screen === DecommissionScreenType.DECOMMISSION_RESULT && (
              <DecommissionResult
                startChatHandler={startChatHandler}
                askAboutSomethingElseHandler={askAboutSomethingElseHandler}
                finishHandler={goBackHandler}
                reportDecommission={reportDecommission}
              />
            )}
          </DecommissionResultContext.Provider>
        </DecommissionRequestContext.Provider>
      </DecommissionScreenContext.Provider>
    </div>
  );
}
